"use client";

import { useCallback, useMemo, useState } from "react";
import type { JobItemDto } from "@artha/api-contracts";
import { evaluateResumeEligibility } from "@artha/utils";

type TrackAndRedirect = (
  job: JobItemDto,
  onRedirect?: (clickAttemptId?: string) => void,
) => Promise<void>;

type UseResumeEligibilityApplyInput = {
  job: JobItemDto | null;
  trackAndRedirect: TrackAndRedirect;
  onRedirect: (clickAttemptId?: string) => void;
  creatorView?: boolean;
};

export function useResumeEligibilityApply({
  job,
  trackAndRedirect,
  onRedirect,
  creatorView = false,
}: UseResumeEligibilityApplyInput) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const eligibility = useMemo(
    () =>
      evaluateResumeEligibility({
        country: job?.country,
        cpa_value: job?.cpa_value,
        rph_value: job?.rph_value,
        work_mode: job?.work_mode,
      }),
    [job?.country, job?.cpa_value, job?.rph_value, job?.work_mode],
  );
  const shouldVerify = Boolean(job && !creatorView && eligibility.eligible);
  const skipFitCheck = eligibility.gate === "collect_only";

  const apply = useCallback(async () => {
    if (!job) return;
    if (shouldVerify) {
      setIsOpen(true);
      return;
    }
    await trackAndRedirect(job, onRedirect);
  }, [job, onRedirect, shouldVerify, trackAndRedirect]);

  const complete = useCallback(async () => {
    if (!job || isRedirecting) return;
    setIsRedirecting(true);
    try {
      await trackAndRedirect(job, onRedirect);
    } finally {
      setIsRedirecting(false);
    }
  }, [isRedirecting, job, onRedirect, trackAndRedirect]);

  return {
    eligibility,
    shouldVerify,
    skipFitCheck,
    isOpen,
    isRedirecting,
    setIsOpen,
    apply,
    complete,
  };
}
